import { SunIcon } from "@phosphor-icons/react/dist/csr/Sun";
import { MoonIcon } from "@phosphor-icons/react/dist/csr/Moon";
import { useLocation } from "react-router";
import { getEffectiveDate } from "@hicya/habits-shared/engine";
import { InMemoryWarning, OfflineIndicator } from "@hicya/ui";

import { useTheme } from "@/theme/useTheme";
import { ScoreRing } from "@/components/ui/score-ring";
import { useHabits } from "@/context/habits-context";
import { useIsInMemoryMode } from "@/db/provider";

import styles from "./header.module.scss";

export function Header() {
  const location = useLocation();
  const isInMemory = useIsInMemoryMode();
  const isTodayRoute = location.pathname === "/" || location.pathname === "/today";

  if (isTodayRoute) {
    return <TodayHeader isInMemory={isInMemory} />;
  }

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>
          {location.pathname === "/stats"
            ? "Stats"
            : location.pathname === "/settings"
              ? "Settings"
              : "Habits"}
        </h1>
        <InMemoryWarning isInMemory={isInMemory} />
        <ThemeToggle />
      </header>
      <OfflineIndicator />
    </>
  );
}

function TodayHeader({ isInMemory }: { isInMemory: boolean }) {
  const { state } = useHabits();

  const today =
    state.today ||
    getEffectiveDate(
      new Date().toISOString(),
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      state.settings.dayBoundaryHour,
    );
  const dateObj = new Date(today + "T12:00:00");
  const formattedDate = dateObj.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  // Count today's scheduled habits
  const todayCompletions = state.completions.filter((c) => c.effective_date === today);
  const completedCount = todayCompletions.filter(
    (c) => c.type === "done" || c.type === "backfill",
  ).length;
  const totalHabits = state.habits.length;
  const progress = totalHabits > 0 ? completedCount / totalHabits : 0;

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Today</h1>
          {state.settings.onboardingCompleted && (
            <span className={styles.date}>{formattedDate}</span>
          )}
        </div>
        <div className={styles.headerRight}>
          <InMemoryWarning isInMemory={isInMemory} />
          <span className={styles.progressSlot}>
            {totalHabits > 0 && (
              <ScoreRing score={progress} size={32} strokeWidth={2.5}>
                <span className={styles.progressCount}>
                  {completedCount}/{totalHabits}
                </span>
              </ScoreRing>
            )}
          </span>
          <ThemeToggle />
        </div>
      </header>
      <OfflineIndicator />
    </>
  );
}

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const light = resolvedTheme === "light";
  return (
    <button
      type="button"
      className={styles.themeToggle}
      onClick={() => setTheme(light ? "dark" : "light")}
      aria-label={light ? "Switch to dark mode" : "Switch to light mode"}
    >
      {light ? <SunIcon size={26} /> : <MoonIcon size={26} />}
    </button>
  );
}
